/**
 * SF-4758: People and weights (admin:priorities:insights). Who has and has not ranked, each
 * person's weight and where it comes from, one person's ranking in a drawer, and the weights.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Drawer, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatShortDate } from '../../../utils/date';
import { teamLabel } from '../../../utils/teams';
import {
  useParticipation,
  usePersonBallot,
  usePriorityItems,
  usePrioritiesSettings,
  type Participant,
  type WeightSource,
} from '../api/prioritiesClient';
import { LoadError, RefreshWarning } from './LoadError';
import { PersonWeightForm, TeamWeightsForm } from './WeightsForms';

const SOURCE_LABELS: Record<WeightSource, string> = {
  person: 'own weight',
  team: 'team weight',
  default: 'default',
};

const PersonBallotDrawer: React.FC<{
  email: string | null;
  listId: string;
  /** SF-5087: only the first N ranked count; undefined while the settings load (no cut drawn). */
  topN?: number;
  onClose: () => void;
}> = ({ email, listId, topN, onClose }) => {
  const ballotQuery = usePersonBallot(email, listId);
  // Every item, so a ranked item that has since closed still shows its title.
  const itemsQuery = usePriorityItems('all', !!email, listId);
  const titles = useMemo(
    () => new Map((itemsQuery.data?.data?.items ?? []).map((i) => [i.id, i])),
    [itemsQuery.data],
  );
  const ballot = ballotQuery.data?.data;
  // The full error only when that read never loaded. A failed refetch (every write refreshes
  // every priorities read) keeps the last ranking and titles, with a warning and a Retry.
  const ballotRefreshFailed = ballotQuery.isError && !!ballot;
  const itemsRefreshFailed = itemsQuery.isError && !!itemsQuery.data;

  return (
    <Drawer open={!!email} onClose={onClose} title={ballot?.name || email} width={480}>
      {(ballotRefreshFailed || itemsRefreshFailed) && (
        <RefreshWarning
          error={ballotRefreshFailed ? ballotQuery.error : itemsQuery.error}
          fallback="This ranking could not be refreshed."
          onRetry={() =>
            Promise.all([
              ballotQuery.isError && ballotQuery.refetch(),
              itemsQuery.isError && itemsQuery.refetch(),
            ])
          }
        />
      )}
      {ballotQuery.isError && !ballot ? (
        <LoadError
          error={ballotQuery.error}
          fallback="This ranking could not be loaded."
          onRetry={ballotQuery.refetch}
        />
      ) : itemsQuery.isError && !itemsQuery.data ? (
        // Without the titles every ranked item would look deleted, so say the read failed.
        <LoadError
          error={itemsQuery.error}
          fallback="The item titles could not be loaded."
          onRetry={itemsQuery.refetch}
        />
      ) : ballotQuery.isLoading || itemsQuery.isLoading ? (
        <Spin />
      ) : ballot && ballot.itemIds.length > 0 ? (
        <Space direction="vertical">
          <Typography.Text type="secondary">
            Last saved {formatShortDate(ballot.updatedAt)}
          </Typography.Text>
          <ol aria-label="Their ranking">
            {ballot.itemIds.map((id, index) => {
              const found = titles.get(id);
              const counts = topN === undefined || index < topN;
              const title = found?.title ?? 'An item that no longer exists';
              return [
                index === topN && (
                  <li
                    key="cut"
                    role="separator"
                    style={{
                      listStyle: 'none',
                      margin: '6px 0',
                      paddingTop: 4,
                      borderTop: '1px dashed #bfbfbf',
                      color: 'rgba(0, 0, 0, 0.45)',
                    }}
                  >
                    Only the top {topN} count
                  </li>
                ),
                <li key={id}>
                  {counts ? title : <Typography.Text type="secondary">{title}</Typography.Text>}
                  {found && found.status !== 'open' && (
                    <Tag style={{ marginLeft: 8 }}>
                      {found.status === 'done' ? 'Done' : 'Removed'}
                    </Tag>
                  )}
                </li>,
              ];
            })}
          </ol>
        </Space>
      ) : (
        <Typography.Text>Has not ranked yet.</Typography.Text>
      )}
    </Drawer>
  );
};

export const PeopleWeightsTab: React.FC<{ listId: string }> = ({ listId }) => {
  // Who has ranked is per list; the weights stay the same for every list.
  const participation = useParticipation(true, listId);
  const settings = usePrioritiesSettings(true);
  const [viewing, setViewing] = useState<string | null>(null);
  const people = participation.data?.data?.people ?? [];

  const columns: ColumnsType<Participant> = [
    { title: 'Name', key: 'name', render: (_, p) => p.name || p.email },
    { title: 'Team', dataIndex: 'team', render: (team) => teamLabel(team) ?? 'No team' },
    {
      title: 'Ranked',
      dataIndex: 'submitted',
      render: (submitted: boolean) => (submitted ? <Tag color="success">Yes</Tag> : <Tag>No</Tag>),
    },
    { title: 'Last updated', dataIndex: 'updatedAt', render: formatShortDate },
    {
      title: 'Weight',
      key: 'weight',
      render: (_, p) => `${p.weight} (${SOURCE_LABELS[p.weightSource]})`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, p) => (
        <Button
          type="link"
          size="small"
          aria-label={`View ranking of ${p.name || p.email}`}
          onClick={() => setViewing(p.email)}
        >
          View ranking
        </Button>
      ),
    },
  ];

  // Same rule for the table and the weights: the full error only when nothing was ever loaded.
  if (participation.isError && !participation.data) {
    return (
      <LoadError
        error={participation.error}
        fallback="People could not be loaded."
        onRetry={participation.refetch}
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {participation.isError && (
        <RefreshWarning
          error={participation.error}
          fallback="People could not be refreshed."
          onRetry={participation.refetch}
        />
      )}
      <Table<Participant>
        rowKey="email"
        loading={participation.isLoading}
        columns={columns}
        dataSource={people}
        pagination={false}
        locale={{ emptyText: 'Nobody has a team yet' }}
      />
      {settings.isError && !settings.data ? (
        // A blank, savable form here would invite overwriting weights nobody has seen.
        <LoadError
          error={settings.error}
          fallback="The weights could not be loaded."
          onRetry={settings.refetch}
        />
      ) : settings.isLoading ? (
        <Card size="small" title="Team weights" loading />
      ) : (
        <>
          {settings.isError && (
            // The weights shown were loaded before, so the form stays (they were seen).
            <RefreshWarning
              error={settings.error}
              fallback="The weights could not be refreshed."
              onRetry={settings.refetch}
            />
          )}
          <TeamWeightsForm settings={settings.data?.data} />
        </>
      )}
      <PersonWeightForm people={people} />
      <PersonBallotDrawer
        email={viewing}
        listId={listId}
        topN={settings.data?.data?.topN}
        onClose={() => setViewing(null)}
      />
    </Space>
  );
};
